import React from 'react';
import { motion } from 'framer-motion';
import { useAnalyticsOverview } from '../../services/analytics';
import KPICard from './KPICard';
import WelcomeBanner from './WelcomeBanner';
import LastDraftCard from './LastDraftCard';
import RealTimeActivityTable from './RealTimeActivityTable';
import HighestRatedCourses from './HighestRatedCourses';
import TopLearnersToday from './TopLearnersToday';
import StaticChartIcon from '@/components/icons/StaticChartIcon';
import { staggerContainer, staggerItem } from '@/lib/motion';

const WelcomeWidget: React.FC = () => {
  const { data: analytics, isLoading, error } = useAnalyticsOverview();

  if (error) {
    return (
      <div className="w-full p-6 border border-error-primary bg-error-light rounded-lg">
        <p className="body-base text-error-dark">
          Failed to load dashboard analytics. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="welcome-widget">
      {/* Welcome Banner + Last Draft */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="md:col-span-3">
          <WelcomeBanner />
        </div>
        <div className="md:col-span-2">
          <LastDraftCard draft={analytics?.last_draft ?? null} />
        </div>
      </div>

      {/* KPI Cards Grid */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
        variants={staggerContainer(0.05)}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={staggerItem}>
          <KPICard
            title="Total Users"
            value={analytics?.total_users ?? 0}
            loading={isLoading}
            icon={<StaticChartIcon />}
            data-testid="kpi-total-users"
          />
        </motion.div>

        <motion.div variants={staggerItem}>
          <KPICard
            title="Active This Month"
            value={analytics?.total_active_users_month ?? 0}
            loading={isLoading}
            icon={<StaticChartIcon color={'rgb(var(--color-secondary))'} />}
            data-testid="kpi-active-month"
          />
        </motion.div>
      </motion.div>

      {/* Real-time Activity + Highest Rated Courses */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
        <div className="md:col-span-3 min-w-0">
          <RealTimeActivityTable
            events={analytics?.real_time_analytics ?? []}
            loading={isLoading}
          />
        </div>
        <div className="md:col-span-2 min-w-0">
          <HighestRatedCourses
            ratings={analytics?.ratings}
            loading={isLoading}
          />
        </div>
      </div>

      {/* Top Learners Today */}
      <TopLearnersToday
        learners={analytics?.top_learners_today ?? []}
        loading={isLoading}
      />
    </div>
  );
};

export default WelcomeWidget;
